/* Setosa core behaviours. Shared by every setosa-* section.

   Owns: countdowns, PDP gallery carousels, quantity steppers, variant pills
   and the sticky mobile buy bar.

   Replaces the three separate countdown scripts that used to live in
   setosa-product.js, setosa-bundle.liquid and setosa-collection.liquid, plus
   the duplicated carousel/qty/sticky logic in setosa-product.js and the
   inline script in setosa-bundle.liquid. */
(function () {
  if (window.__setosaCore) return;
  window.__setosaCore = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function all(sel, root) { return [].slice.call((root || document).querySelectorAll(sel)); }

  /* ---------------------------------------------------------------- countdown */

  /* Every setosa-cd countdown is a rolling 2-hour urgency loop: it counts
     down to zero, then immediately restarts at 2:00:00, forever. The
     merchant-set deadline only gates whether a countdown renders at all
     (see snippets/setosa-countdown.liquid); its date value is not used for
     timing. The reset point is shared site-wide via localStorage, so every
     countdown a visitor sees stays in sync and a page refresh mid-cycle
     resumes rather than restarting. */

  var ROLL_MS = 2 * 60 * 60 * 1000;
  var ROLL_KEY = 'setosa_cd_deadline';

  function rollingDeadline() {
    var now = Date.now();
    var stored = 0;
    try { stored = parseInt(localStorage.getItem(ROLL_KEY) || '0', 10); } catch (e) {}
    if (!stored || stored <= now) {
      stored = now + ROLL_MS;
      try { localStorage.setItem(ROLL_KEY, String(stored)); } catch (e) {}
    }
    return stored;
  }

  function initCountdowns() {
    var timers = [];
    all('[data-setosa-cd]').forEach(function (el) {
      if (el.__setosaCd) return;
      if (!el.getAttribute('data-deadline')) return;
      el.__setosaCd = true;
      timers.push({
        el: el,
        d: el.querySelector('[data-d]'), h: el.querySelector('[data-h]'),
        m: el.querySelector('[data-m]'), s: el.querySelector('[data-s]')
      });
    });
    if (!timers.length) return;

    function tick() {
      var left = rollingDeadline() - Date.now();
      if (left < 0) left = 0;
      var dd = pad(Math.floor(left / 86400000));
      var hh = pad(Math.floor(left / 3600000) % 24);
      var mm = pad(Math.floor(left / 60000) % 60);
      var ss = pad(Math.floor(left / 1000) % 60);
      timers.forEach(function (t) {
        if (t.d) t.d.textContent = dd;
        if (t.h) t.h.textContent = hh;
        if (t.m) t.m.textContent = mm;
        if (t.s) t.s.textContent = ss;
      });
    }
    tick();
    setInterval(tick, 1000);
  }

  /* ----------------------------------------------------------------- carousel */

  function initCarousels() {
    all('[role="region"][aria-roledescription="carousel"]').forEach(function (region) {
      if (region.__setosaCarousel) return;
      var track = region.querySelector('[data-setosa-track]');
      if (!track) return;
      region.__setosaCarousel = true;

      var slides = track.children;
      var dotsWrap = region.querySelector('[data-setosa-dots]');

      function step() {
        if (!slides.length) return 0;
        var gap = parseFloat(getComputedStyle(track).columnGap || 0) || 0;
        return slides[0].getBoundingClientRect().width + gap;
      }
      function go(dir) {
        track.scrollBy({ left: dir * step(), behavior: reduce ? 'auto' : 'smooth' });
      }

      var prev = region.querySelector('[data-setosa-prev]');
      var next = region.querySelector('[data-setosa-next]');
      if (prev) prev.addEventListener('click', function () { go(-1); });
      if (next) next.addEventListener('click', function () { go(1); });

      if (dotsWrap && slides.length > 1) {
        for (var i = 0; i < slides.length; i++) {
          (function (i) {
            var b = document.createElement('button');
            b.type = 'button';
            b.setAttribute('aria-label', 'Go to image ' + (i + 1));
            b.addEventListener('click', function () {
              track.scrollTo({ left: i * step(), behavior: reduce ? 'auto' : 'smooth' });
            });
            dotsWrap.appendChild(b);
          })(i);
        }
        var ticking = false;
        function mark() {
          var idx = Math.round(track.scrollLeft / (step() || 1)) || 0;
          [].forEach.call(dotsWrap.children, function (d, j) {
            d.setAttribute('aria-current', j === idx ? 'true' : 'false');
          });
          ticking = false;
        }
        track.addEventListener('scroll', function () {
          if (!ticking) { ticking = true; requestAnimationFrame(mark); }
        }, { passive: true });
        mark();
      }
    });
  }

  /* -------------------------------------------------------------- quantity */

  function initQty() {
    all('[data-setosa-qty]').forEach(function (input) {
      if (input.__setosaQty) return;
      input.__setosaQty = true;
      var box = input.closest('.setosa-qty') || input.parentElement;
      var minus = box.querySelector('[data-setosa-minus]');
      var plus = box.querySelector('[data-setosa-plus]');
      if (minus) minus.addEventListener('click', function () {
        var n = parseInt(input.value, 10) || 1;
        if (n > 1) input.value = n - 1;
      });
      if (plus) plus.addEventListener('click', function () {
        input.value = (parseInt(input.value, 10) || 1) + 1;
      });
    });
  }

  /* -------------------------------------------------------------- variants */

  function initVariants() {
    all('[data-setosa-buybox]').forEach(function (box) {
      if (box.__setosaVariants) return;
      var select = box.querySelector('[data-setosa-id]');
      if (!select) return;
      box.__setosaVariants = true;

      var pills = all('[data-setosa-opt]', box);
      var priceEl = box.querySelector('[data-setosa-price]');
      var compareEl = box.querySelector('[data-setosa-compare]');
      var addBtn = box.querySelector('[data-setosa-add]');
      var addText = box.querySelector('[data-setosa-addtext]');

      function sync() {
        var opt = select.options[select.selectedIndex];
        if (!opt) return;
        if (priceEl) priceEl.textContent = opt.getAttribute('data-price') || priceEl.textContent;
        var cmp = opt.getAttribute('data-compare');
        if (compareEl) {
          if (cmp && cmp !== '' && cmp !== opt.getAttribute('data-price')) {
            compareEl.textContent = cmp;
            compareEl.removeAttribute('hidden');
          } else {
            compareEl.setAttribute('hidden', '');
          }
        }
        var available = opt.getAttribute('data-available') === 'true';
        if (addBtn) addBtn.disabled = !available;
        if (addText) addText.textContent = available
          ? (addBtn.getAttribute('data-cta') || addText.textContent)
          : 'Sold Out';
      }

      pills.forEach(function (pill) {
        pill.addEventListener('click', function () {
          var val = pill.getAttribute('data-setosa-value');
          var group = pill.closest('.setosa-opt__pills');
          if (group) all('.setosa-pill', group).forEach(function (p) { p.classList.remove('is-active'); });
          pill.classList.add('is-active');

          var label = pill.closest('.setosa-opt').querySelector('[data-setosa-optval]');
          if (label) label.textContent = val;

          // Build the wanted option set, then find the matching variant.
          var wanted = all('.setosa-opt', box).map(function (o) {
            var active = o.querySelector('.setosa-pill.is-active');
            return active ? active.getAttribute('data-setosa-value') : null;
          });
          for (var i = 0; i < select.options.length; i++) {
            var title = select.options[i].textContent.trim();
            var parts = title.split(' / ').map(function (s) { return s.trim(); });
            var match = wanted.every(function (w, k) { return w === null || parts[k] === w; });
            if (match) { select.selectedIndex = i; break; }
          }
          select.dispatchEvent(new Event('change', { bubbles: true }));
          sync();
        });
      });

      select.addEventListener('change', sync);
      sync();
    });
  }

  /* ---------------------------------------------------------------- sticky */

  function initSticky() {
    all('[data-setosa-sticky]').forEach(function (sticky) {
      if (sticky.__setosaSticky) return;
      var scope = sticky.closest('section') || document;
      var add = scope.querySelector('[data-setosa-add]');
      if (!add) return;
      sticky.__setosaSticky = true;

      var stickyBtn = sticky.querySelector('[data-setosa-stickyadd]');
      if (stickyBtn) stickyBtn.addEventListener('click', function () { add.click(); });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            sticky.classList.toggle('is-show', !e.isIntersecting && e.boundingClientRect.top < 0);
          });
        }, { threshold: 0 }).observe(add);
      }
    });
  }

  /* ----------------------------------------------------------- scroll reveal */

  function initReveal() {
    var els = all('.setosa-reveal').filter(function (el) { return !el.__setosaReveal; });
    if (!els.length) return;
    els.forEach(function (el) { el.__setosaReveal = true; });
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------------ boot */

  function boot() {
    initCountdowns();
    initCarousels();
    initQty();
    initVariants();
    initSticky();
    initReveal();
  }

  function start() {
    boot();
    // Theme editor re-renders sections without a page load.
    document.addEventListener('shopify:section:load', boot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
